import React, {useMemo} from 'react';
import {ChevronLeft, ChevronRight, Loader2} from 'lucide-react';
import {DataTableColumn} from '@/interface';
import {cn} from '@/utils/common_functions';
import {Text} from '@/ui-kits';
import { colors } from '@/theme';

// Helper function to convert ColumnWidth to style object
export const getColumnWidthStyles = (width: DataTableColumn<any>['width']): React.CSSProperties => {
  return {
    minWidth: width.minWidth,
    width: width.width || 'auto',
    maxWidth: width.maxWidth,
  };
};

interface DataTableProps {
  data: any[];
  columns: DataTableColumn<any>[];
  totalPages: number;
  currentPage: number;
  totalResult: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  errorMessage?: string;
  loading?: boolean;
  showFooter?: boolean;
  stickyHeader?: boolean;
}

export const DataTable: React.FC<DataTableProps> = props => {
  const {data = [], columns = [], onPageChange, currentPage, totalPages, totalResult, errorMessage, loading, showFooter = true, stickyHeader = true} = props;

  function pageNumbers() {
    const pageRange = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        pageRange.push(i);
      }
      return pageRange;
    }
    if (currentPage === 1 || currentPage === 2) {
      return [1, 2, 3, 4];
    }
    if (currentPage === totalPages || currentPage === totalPages - 1) {
      return [totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1];
  }

  const getAlignClass = (align: DataTableColumn<any>['align']) => {
    switch (align) {
      case 'center':
        return 'text-center justify-center';
      case 'right':
        return 'text-right justify-end';
      default:
        return 'text-left justify-start';
    }
  };

  const [rangeStart, rangeEnd] = useMemo(() => {
    if (totalResult === 0 || data.length === 0) {
      return [0, 0];
    }

    let start = (currentPage - 1) * data.length + 1;
    let end = start + data.length - 1;

    if (end > totalResult) {
      end = totalResult;
    }
    return [start, end];
  }, [currentPage, data.length, totalResult]);

  const empty = useMemo(() => {
    return data.length === 0;
  }, [data.length]);

  const stickyColStyle = (col_type : 'header' | 'body'): React.CSSProperties => ({
    position: 'sticky',
    left: 0,
    background: col_type === 'header' ? colors.palette.ui_sky_blue : colors.palette.ui_bg_sky, // match your header bg or use 'inherit'
    zIndex: 3,
    // borderRightWidth: 1,
    // borderRightStyle: 'solid',
    // borderRightColor: '#e5e7eb',
    // boxShadow: '10px 10px 10px 10px black'
  });

  return (
    <div className="w-full border border-ui_input_border rounded-xl overflow-hidden shadow-sm font-sans">
      <div className={cn('overflow-x-auto relative data-table-scroll', stickyHeader && 'overflow-y-auto max-h-[600px]')}>
        <table className="w-full min-w-[800px]">
          {/* Header */}
          <thead
            className={cn(
              'bg-ui_sky_blue border-b border-gray-200 text-ui_blue text-xs font-bold uppercase tracking-wider',
              stickyHeader && 'sticky top-0 z-10',
            )}>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  className={`px-4 text-nowrap! py-4 ${getAlignClass(col.headerAlign || col.align)} ${col.headerClassName || ''} ${
                    index === 0 ? 'sticky-col' : ''
                  }`}
                  style={
                    index === 0
                      ? {...(empty ? {textWrap: 'nowrap'} : getColumnWidthStyles(col.width)), ...stickyColStyle('header')}
                      : empty
                      ? {textWrap: 'nowrap'}
                      : getColumnWidthStyles(col.width)
                  }>
                  {typeof col.title === 'string' ? (
                    <Text className={cn('flex size-full items-center', getAlignClass(col.headerAlign || col.align), empty && 'text-nowrap')}>{col.title}</Text>
                  ) : (
                    col.title
                  )}
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="bg-white divide-y divide-ui_input_border">
            {data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-slate-50 transition-colors duration-150">
                  {columns.map((col, colIndex) => (
                    <td
                      key={`${rowIndex}-${colIndex}`}
                      className={`px-4 py-4 text-sm text-ui_input_border_dark font-FigtreeSemiBold whitespace-nowrap ${getAlignClass(col.align)} ${
                        colIndex === 0 ? 'sticky-col' : ''
                      }`}
                      style={colIndex === 0 ? {...getColumnWidthStyles(col.width), ...stickyColStyle('body')} : getColumnWidthStyles(col.width)}>
                      {col.render(row, col.width)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="font-FigtreeSemiBold px-6 py-10 text-center text-ui_input_border_dark !text-lg">
                  {errorMessage || 'No data available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <Loader2 className="size-6 animate-spin text-ui_blue" />
          </div>
        )}
      </div>
      {/* Footer */}
      {showFooter && (
        <div className="bg-ui_sky_blue border-t border-gray-200 px-2 sm:px-4 md:px-6 py-4 flex items-center justify-between">
          <Text variant="small" className="text-ui_blue font-medium">
            Showing {rangeStart} to {rangeEnd} of {totalResult} results
          </Text>
          <div className="flex">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage == 1}
              className="p-1 w-[30px] h-[26px] border-[1px] border-ui_input_border bg-white hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent flex items-center justify-center">
              <ChevronLeft size={20} className="text-slate-600" />
            </button>
            {pageNumbers().map(page => (
              <button
                className={cn(
                  'border-[1px] border-ui_input_border bg-white w-[34px] h-[26px] flex flex-col items-center justify-center',
                  page === currentPage && 'bg-ui_sky_blue',
                )}
                key={page}
                onClick={() => onPageChange(page)}>
                <Text variant="small" className="font-FigtreeLight text-ui_blue">
                  {page}
                </Text>
              </button>
            ))}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-1 w-[30px] h-[26px] border-[1px] border-ui_input_border bg-white hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent flex items-center justify-center">
              <ChevronRight size={20} className="text-slate-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
